// HandIntroAnimation.jsx
// Flies cards one-at-a-time from the deck to each seat, then calls onComplete.
// Uses exact card-landing positions derived from seat.css and hero-seat.css.
//
// Coordinate system: table-wrapper (290 × 430 px), position: absolute, overflow: visible.
//
// BTN → seat-2: top:77px left:-20px  → cards-container top:-2 left:45  → (25, 75) in wrapper
// SB  → seat-4: top:77px left:252px  → cards-container top:-2 left:-35 → (217, 75)
// BB  → hero-seat top-left:(107,386) → cards-container top:-50 left:-8  → (99, 336)
//
// Villain card size matches .seat.seat-on .card  { width:26px height:38px }
// Hero    card size matches .hero-seat   .card  { width:42px height:60px }
// card-2 offsets:  villain translateX(15px), hero translateX(35px)

import React, { useState, useEffect, useRef } from 'react';
import './HandIntroAnimation.css';

const DECK = { x: 145, y: 215 }; // centre of green oval in table-wrapper coords

const CARD_REST = {
  BTN: { 1: { x: 25,  y: 75  }, 2: { x: 40,  y: 75  } },
  SB:  { 1: { x: 217, y: 75  }, 2: { x: 232, y: 75  } },
  BB:  { 1: { x: 99,  y: 336 }, 2: { x: 134, y: 336 } },
};

const V_W = 26, V_H = 38; // villain card dimensions (matches seat.css .card)
const H_W = 42, H_H = 60; // hero    card dimensions (matches hero-seat.css .card)

// Classic Texas Hold'em deal order starting from SB
const DEAL_ORDER = [
  { position: 'SB',  cardNum: 1, isHero: false },
  { position: 'BB',  cardNum: 1, isHero: true  },
  { position: 'BTN', cardNum: 1, isHero: false },
  { position: 'SB',  cardNum: 2, isHero: false },
  { position: 'BB',  cardNum: 2, isHero: true  },
  { position: 'BTN', cardNum: 2, isHero: false },
];

const FLIGHT_MS = 280; // card flight duration
const PAUSE_MS  = 300; // gap between cards

const HandIntroAnimation = ({ heroCards, onComplete }) => {
  const [dealIdx,      setDealIdx]      = useState(0);
  const [flyingCard,   setFlyingCard]   = useState(null);
  const [restingCards, setRestingCards] = useState([]);
  const [isDone,       setIsDone]       = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (isDone) return;

    // All cards dealt — brief pause then hand off to parent
    if (dealIdx >= DEAL_ORDER.length) {
      const t = setTimeout(() => {
        if (mountedRef.current) {
          setIsDone(true);
          onComplete?.();
        }
      }, 350);
      return () => clearTimeout(t);
    }

    const step = DEAL_ORDER[dealIdx];
    const rest = CARD_REST[step.position][step.cardNum];
    const w = step.isHero ? H_W : V_W;
    const h = step.isHero ? H_H : V_H;

    const card = {
      id:        `fly-${dealIdx}`,
      fromX:     DECK.x - w / 2,
      fromY:     DECK.y - h / 2,
      toX:       rest.x,
      toY:       rest.y,
      isHero:    step.isHero,
      position:  step.position,
      cardNum:   step.cardNum,
      w, h,
      animating: false,
    };

    setFlyingCard(card);

    // Double-rAF forces layout commit before transition fires
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (mountedRef.current) setFlyingCard(f => f ? { ...f, animating: true } : null);
    }));

    const land = setTimeout(() => {
      if (!mountedRef.current) return;
      setRestingCards(prev => [...prev, {
        id:       `rest-${dealIdx}`,
        x: rest.x, y: rest.y, w, h,
        isHero:   step.isHero,
        cardNum:  step.cardNum,
        position: step.position,
      }]);
      setFlyingCard(null);
      setDealIdx(d => d + 1);
    }, FLIGHT_MS + PAUSE_MS);

    return () => clearTimeout(land);
  }, [dealIdx, isDone]);

  if (isDone) return null;

  return (
    <div className="hia-overlay" aria-hidden="true">

      {/* ── Resting cards (landed at seats) ── */}
      {restingCards.map(card => (
        <div
          key={card.id}
          className={`hia-card ${card.isHero ? 'hia-card--hero' : 'hia-card--villain'}`}
          style={{ left: card.x, top: card.y, width: card.w, height: card.h }}
        >
          {card.isHero && heroCards && (
            <span className="hia-card__value">
              {card.cardNum === 1 ? heroCards.card1Value : heroCards.card2Value}
            </span>
          )}
        </div>
      ))}

      {/* ── Flying card (one in flight at a time) ── */}
      {flyingCard && (
        <div
          className={`hia-card hia-card--flying ${flyingCard.isHero ? 'hia-card--hero' : 'hia-card--villain'}`}
          style={{
            left:   flyingCard.fromX,
            top:    flyingCard.fromY,
            width:  flyingCard.w,
            height: flyingCard.h,
            transform: flyingCard.animating
              ? `translate(${flyingCard.toX - flyingCard.fromX}px, ${flyingCard.toY - flyingCard.fromY}px) rotate(0deg)`
              : 'translate(0,0) rotate(-12deg)',
          }}
        />
      )}
    </div>
  );
};

export default HandIntroAnimation;
